import { scrollToElement } from './scrollToElement';

/**
 * Scrolls smoothly to the top-most (visually first) field that has a validation error.
 * 
 * - Called ONLY from submit handlers after setting error state (never onChange or useEffect).
 * - Uses DOM position (getBoundingClientRect) to determine "top most".
 * - Works for per-field error maps and falls back gracefully for global errors.
 * - Supports modals and sticky headers.
 * - Does NOT modify any validation logic, error shapes, or form behavior.
 */
export function scrollToFirstErrorField(
  errors?: Record<string, unknown> | null,
  options?: { delay?: number; headerOffset?: number }
): void {
  const delay = options?.delay ?? 180; // Give React + layout time, especially inside modals and after setState in validateForm
  const headerOffset = options?.headerOffset;

  // Always attempt scroll (even if called without errors arg for pure DOM fallback).
  // Only early-exit if we were explicitly given an *empty* errors object.
  if (errors !== undefined && (!errors || Object.keys(errors).length === 0)) {
    return;
  }

  const doScroll = () => {
    const getScrollParent = (element: HTMLElement): HTMLElement => {
      let parent = element.parentElement;
      while (parent) {
        const style = window.getComputedStyle(parent);
        if (/(auto|scroll)/.test(style.overflowY) || /(auto|scroll)/.test(style.overflow)) {
          return parent;
        }
        parent = parent.parentElement;
      }
      return document.scrollingElement as HTMLElement || document.documentElement;
    };

    // Find the most relevant form (first one, or the one containing potential errored fields)
    const allForms = Array.from(document.querySelectorAll('form'));
    let targetForm = allForms[0] || document;

    // Core: Collect potential field roots IN DOM SOURCE ORDER (within the form or document)
    // This is the reliable "top-down" - DOM order matches authoring/visual top-to-bottom.
    const selector = 'input, select, textarea, [data-error-field], [data-field]';
    let potentialFields = Array.from(targetForm.querySelectorAll(selector));

    // If we have a specific form and multiple, prefer the first form that has matching errored fields
    if (allForms.length > 1 && errors && Object.keys(errors).length > 0) {
      for (const f of allForms) {
        const fieldsInForm = Array.from(f.querySelectorAll(selector));
        const hasErrored = fieldsInForm.some(el => {
          const k = el.getAttribute('name') || el.getAttribute('data-error-field') || el.getAttribute('data-field');
          return k && errors && k in errors;
        });
        if (hasErrored) {
          targetForm = f;
          potentialFields = fieldsInForm;
          break;
        }
      }
    }

    // Build ordered list of errored field roots (first match in DOM order wins)
    const erroredFieldsInOrder: HTMLElement[] = [];
    const errorKeys = errors ? Object.keys(errors) : [];

    for (const el of potentialFields) {
      const htmlEl = el as HTMLElement;
      if (!htmlEl.offsetParent) continue; // not visible

      let isErrored = false;

      // 1. By key (name or data-*)
      const key = htmlEl.getAttribute('name') || htmlEl.getAttribute('data-error-field') || htmlEl.getAttribute('data-field');
      if (key && errorKeys.includes(key)) {
        isErrored = true;
      }

      // 2. By error styling on self or near (for global or custom)
      if (!isErrored) {
        const hasErrorStyle = 
          htmlEl.className.includes('border-red-300') ||
          htmlEl.className.includes('bg-red-50') ||
          htmlEl.className.includes('text-red-700') ||
          (htmlEl.parentElement && (
            htmlEl.parentElement.className.includes('border-red-300') ||
            htmlEl.parentElement.className.includes('bg-red-50')
          )) ||
          !!htmlEl.querySelector('[role="alert"], .text-red-500, .text-red-700, div.bg-red-50') ||
          (htmlEl.parentElement && !!htmlEl.parentElement.querySelector('[role="alert"], .text-red-500, .text-red-700, div.bg-red-50'));
        if (hasErrorStyle) isErrored = true;
      }

      if (isErrored) {
        erroredFieldsInOrder.push(htmlEl);
      }
    }

    if (erroredFieldsInOrder.length === 0) {
      // Fallback for global errors or no matches: first visible field or top of first form
      const firstFormEl = allForms[0] || document.querySelector('form');
      if (firstFormEl) {
        const firstField = firstFormEl.querySelector('input, select, textarea, [data-error-field]') as HTMLElement | null;
        const scrollEl = (firstField || firstFormEl) as HTMLElement;
        const scroller = getScrollParent(scrollEl);
        const isDoc =
          scroller === document.documentElement ||
          scroller === document.body;
        if (isDoc) {
          scrollToElement(scrollEl, {
            behavior: 'smooth',
            block: 'start',
            ...(headerOffset != null ? { offsetTop: headerOffset } : {}),
          });
        } else {
          const rect = scrollEl.getBoundingClientRect();
          const sRect = scroller.getBoundingClientRect();
          const top = scroller.scrollTop + (rect.top - sRect.top) - (headerOffset ?? 24);
          scroller.scrollTo({ top: Math.max(0, top), behavior: 'smooth' });
        }
      }
      return;
    }

    // Ensure strictly document source order (in case querySelectorAll order or dynamic inserts are unexpected)
    // Specs etc are last in source, so this guarantees earlier fields win if they have errors.
    erroredFieldsInOrder.sort((a, b) => {
      if (a === b) return 0;
      const pos = a.compareDocumentPosition(b);
      if (pos & Node.DOCUMENT_POSITION_FOLLOWING) return -1;
      if (pos & Node.DOCUMENT_POSITION_PRECEDING) return 1;
      return 0;
    });

    // Take the FIRST in DOM order - this is strictly top-down and reliable
    let rawTarget = erroredFieldsInOrder[0];

    // 5. Resolve to the best element to scroll to.
    // Prefer visible form controls. Special handling for custom "field groups" (images dashed box, categories grid, etc.)
    // that use wrapper styling instead of on the input.
    let scrollTarget: HTMLElement = rawTarget;

    const isHiddenInput = (el: Element | null): boolean =>
      el instanceof HTMLInputElement && (el.type === 'hidden' || !el.offsetParent);

    if (rawTarget.matches('input,select,textarea') && isHiddenInput(rawTarget)) {
      // Never target hidden inputs; fall back to their visual container
      scrollTarget = rawTarget.closest('div, label') || rawTarget.parentElement || rawTarget;
    } else if (rawTarget.matches('input,select,textarea')) {
      // Scroll the labeled group so the label is not tucked under the sticky header / browser chrome.
      const marked = rawTarget.closest('[data-error-field]');
      if (marked instanceof HTMLElement) {
        scrollTarget = marked;
      } else {
        let wrap = rawTarget.parentElement;
        const form = rawTarget.closest('form');
        while (wrap && wrap !== form) {
          if (wrap.querySelector('label')) {
            scrollTarget = wrap;
            break;
          }
          wrap = wrap.parentElement;
        }
        if (scrollTarget === rawTarget && rawTarget.parentElement) {
          scrollTarget = rawTarget.parentElement;
        }
      }
    } else if (!rawTarget.matches('input,select,textarea')) {
      // Look for a visible control inside or in the group
      let foundControl = rawTarget.querySelector('input,select,textarea:not([type="hidden"])') ||
                         rawTarget.closest('div, label')?.querySelector('input,select,textarea:not([type="hidden"])');

      if (foundControl && !isHiddenInput(foundControl)) {
        scrollTarget = foundControl as HTMLElement;
      } else {
        // For custom styled error containers (e.g. image dropzone with border-red), use the container itself
        const hasErrorStyling = rawTarget.classList.contains('border-red-300') ||
                                rawTarget.className.includes('bg-red') ||
                                rawTarget.className.includes('border-red');
        scrollTarget = hasErrorStyling ? rawTarget : rawTarget;
      }
    }

    const scroller = getScrollParent(scrollTarget);

    const targetRect = scrollTarget.getBoundingClientRect();

    // For main document scrolling (user-facing forms), use window for reliability
    const isDocumentScroller = scroller === document.documentElement ||
                               scroller === document.body ||
                               scroller.scrollHeight <= (window.innerHeight * 1.2);

    if (isDocumentScroller) {
      scrollToElement(scrollTarget, {
        behavior: 'smooth',
        block: 'start',
        ...(headerOffset != null ? { offsetTop: headerOffset } : {}),
      });
    } else {
      // Internal scroll container (e.g. admin main with overflow-auto)
      const scrollerRect = scroller.getBoundingClientRect();
      const currentScroll = scroller.scrollTop;
      const inset = headerOffset ?? 24;
      const targetScroll = currentScroll + (targetRect.top - scrollerRect.top) - inset;
      scroller.scrollTo({
        top: Math.max(0, targetScroll),
        behavior: 'smooth'
      });
    }

    // 7. Focus after a bit (smooth scroll needs time)
    setTimeout(() => {
      try {
        const focusEl =
          rawTarget.matches('input,select,textarea') && !isHiddenInput(rawTarget)
            ? rawTarget
            : scrollTarget.querySelector('input,select,textarea');
        if (focusEl instanceof HTMLInputElement ||
            focusEl instanceof HTMLSelectElement ||
            focusEl instanceof HTMLTextAreaElement) {
          focusEl.focus({ preventScroll: true });
        }
      } catch {}
    }, 350);
  };

  // Use requestAnimationFrame + setTimeout to wait for React commit + browser layout/paint reliably
  const rafScroll = () => {
    requestAnimationFrame(() => {
      setTimeout(doScroll, 40);
    });
  };

  setTimeout(rafScroll, delay);
}
